import { useContext, useEffect, useState, useMemo } from "react";
import { Button, Dialog, Divider, Stack, Typography } from "@mui/material";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { BubbleChatQuestionIcon, Cancel01Icon, HourglassIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { sanitizePhoneForWhatsapp } from "@/utils/phones";
import { isEmpty } from "lodash";
import { useRejectOrderMutation, useRescheduleOrderTimeoutMutation } from "@/features/organizations/api/ordersApi";
import { Order, OrderStatus } from "@/types";
import useSnackbar from "@/hooks/useSnackbar";
import LoadingButton from "@mui/lab/LoadingButton";
import useTimeCounter from "@/hooks/useTimeCounter";

interface Props {
  order: Order;
  onGoBack?: () => void;
}

const TimeoutDialog = ({ order, onGoBack }: Props) => {
  const { organization, branch } = useContext(WebsiteContext);
  const [rejectOrder] = useRejectOrderMutation();
  const [rescheduleOrderTimeout, { isLoading: isRescheduling }] = useRescheduleOrderTimeoutMutation();
  const [openSnackbar] = useSnackbar();
  const { now } = useTimeCounter();
  const [open, setOpen] = useState(false);
  const [wasTimeExtended, setWasTimeExtended] = useState(false);

  const selecetedBranch = useMemo(() => {
    return branch?.find((b) => b.id === order.branch?.id);
  }, [branch, order.branch?.id]);

  const whatsappSelected = order.branch?.id !== null ? selecetedBranch?.whatsapp : organization?.whatsapp;

  const handleSendMessage = async () => {
    if (!whatsappSelected) return;

    const whatsappClean = sanitizePhoneForWhatsapp(whatsappSelected);
    if (isEmpty(whatsappClean)) return;

    const urlencodedtext = `%20Necesito%20ayuda%20con%20mi%20pedido%20No. ${order?.number}`;

    window.open(`https://wa.me/${whatsappClean}?text=${urlencodedtext}`);
  };

  const handleReschedule = async () => {
    if (!organization?.id || !order?.id) return;

    try {
      await rescheduleOrderTimeout({
        organizationId: organization.id,
        orderId: order.id,
      }).unwrap();
      openSnackbar("5 minutos adicionales agregados", "success");
      setWasTimeExtended(true);
      setOpen(false);
    } catch (error) {
      console.error(error);
      openSnackbar("Error al solicitar más tiempo", "error");
    }
  };

  const handleCancelOrder = async () => {
    if (!organization?.id || !order?.id) return;

    try {
      await rejectOrder({
        organizationId: organization.id,
        orderId: order.id,
        order,
        rejectedReason: "El cliente se cansó de esperar",
      }).unwrap();
      openSnackbar("Pedido cancelado", "success");
      onGoBack?.();
    } catch (error) {
      console.error(error);
      openSnackbar("Error al cancelar el pedido", "error");
    }
  };

  const handleClose = (_: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") return;
    setOpen(false);
  };

  useEffect(() => {
    if (!order.expiresAt) return;

    const alertTime = order.expiresAt - (10 * 60 * 1000);

    if (order.status === OrderStatus.REVIEWING && !wasTimeExtended && now >= alertTime) {
      setOpen(true);
    }
  }, [order.expiresAt, order.status, now, wasTimeExtended]);

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={true}>
      <Stack p={3} spacing={2}>
        <Stack py={4} alignItems="center">
          <img alt="Pedido no confirmado" width={115} src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Ftime-icon.webp?alt=media&token=5dc8321c-3930-4e67-a70d-90e013d9803f" />
        </Stack>

        <Typography textAlign="center" variant="h3">Algo anda mal</Typography>
        <Typography textAlign="center" variant="body1">Tu pedido no ha sido confirmado en los primeros 5 minutos. Para evitar esperas innecesarias te sugerimos:</Typography>
        {organization?.whatsapp ? (
          <Button size="large" color="success" startIcon={<HugeiconsIcon icon={BubbleChatQuestionIcon} />} onClick={handleSendMessage}>
            Contáctanos
          </Button>
        ) : null}
        <LoadingButton
          loading={isRescheduling}
          size="large"
          variant="outlined"
          startIcon={<HugeiconsIcon icon={HourglassIcon} />}
          onClick={handleReschedule}
        >
          Esperar 5 min mas
        </LoadingButton>
        <Divider>ó</Divider>
        <Button size="large" variant="outlined" startIcon={<HugeiconsIcon icon={Cancel01Icon} />} onClick={handleCancelOrder}>
          Cancelar pedido
        </Button>
      </Stack>
    </Dialog>
  );
};

export default TimeoutDialog;
